
import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  expandedGroups: Set<string>;
  setIsCollapsed: (collapsed: boolean) => void;
  setIsMobileOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  toggleGroup: (groupId: string) => void;
  expandGroup: (groupId: string) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('sidebar-expanded-groups');
    return saved ? new Set(JSON.parse(saved)) : new Set(['financeiro', 'planejamento']);
  });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('sidebar-expanded-groups', JSON.stringify([...expandedGroups]));
  }, [expandedGroups]);

  const toggleSidebar = () => {
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      setIsMobileOpen(!isMobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const expandGroup = (groupId: string) => {
    setExpandedGroups(prev => new Set([...prev, groupId]));
  };

  const value = useMemo(
    () => ({
      isCollapsed,
      isMobileOpen,
      expandedGroups,
      setIsCollapsed,
      setIsMobileOpen,
      toggleSidebar,
      toggleGroup,
      expandGroup,
    }),
    [isCollapsed, isMobileOpen, expandedGroups]
  );

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};
